import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { useOptimisticRealtime } from '../../../hooks/useOptimisticRealtime';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ModifierRendezVousModal from '../../../components/feature/ModifierRendezVousModal';

interface RendezVous {
  id: string;
  patient_id: string;
  medecin_id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  motif: string | null;
  statut: string;
  patients: {
    id: string;
    nom: string;
    prenom: string;
    telephone?: string;
    email?: string;
  } | null;
  medecins: {
    id: string;
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

const JOURS_SEMAINE = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export default function AdminRendezVousPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isTrueSuperAdmin = userRoles?.includes('super_admin') ?? false;
  const [loading, setLoading] = useState(true);
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMedecin, setFilterMedecin] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [selectedRdvToEdit, setSelectedRdvToEdit] = useState<RendezVous | null>(null);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [showNouveauRdvModal, setShowNouveauRdvModal] = useState(false);
  const { mutate: offlineMutate } = useOfflineMutation<any>();
  const { clearOptimisticCache } = useOptimisticRealtime();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }

    loadData();

    const channel = supabase
      .channel('admin_rdv_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rendez_vous' }, () => {
        loadRendezVous();
        clearOptimisticCache();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, role, authLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadRendezVous(), loadMedecins()]);
    } finally {
      setLoading(false);
    }
  };

  const loadRendezVous = async () => {
    const { data } = await supabase
      .from('rendez_vous')
      .select(`*, patients(id, nom, prenom, telephone, email), medecins(id, nom, prenom, specialite)`)
      .order('date_rdv', { ascending: true })
      .order('heure_debut', { ascending: true });
    if (data) {
      const now = new Date();
      const toUpdate: string[] = [];
      data.forEach((rdv: any) => {
        if (rdv.date_rdv && rdv.heure_debut && ['en_attente', 'confirme'].includes(rdv.statut)) {
          const dt = new Date(`${rdv.date_rdv}T${rdv.heure_debut}`);
          if (dt < now) { toUpdate.push(rdv.id); rdv.statut = 'manque'; }
        }
      });
      if (toUpdate.length > 0) {
        await supabase.from('rendez_vous').update({ statut: 'manque' }).in('id', toUpdate);
      }
      setRendezVous(data);
    }
  };

  const loadMedecins = async () => {
    const { data } = await supabase.from('medecins').select('id, nom, prenom, specialite').order('nom');
    if (data) setMedecins(data);
  };

  const handleAnnulerRdv = async (rdv: RendezVous) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) return;
    try {
      await offlineMutate({
        table: 'rendez_vous',
        operation: 'UPDATE',
        recordId: rdv.id,
        data: { statut: 'annule', date_modification: new Date().toISOString() },
        metadata: { summary: `Annulation RDV — ${rdv.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          await supabase.from('rendez_vous').update({ statut: 'annule', date_modification: new Date().toISOString() }).eq('id', rdv.id);
          return { id: rdv.id };
        },
      });
      await loadRendezVous();
      clearOptimisticCache();
    } catch (e) { alert('Erreur lors de l\'annulation'); }
  };

  const handleSupprimerRdv = async (rdv: RendezVous) => {
    if (!confirm('ATTENTION : Cette action supprimera définitivement ce rendez-vous et TOUTES les données associées (consultation, ordonnance, bon de labo, fiche de soin, suivi). Continuer ?')) return;
    try {
      await offlineMutate({
        table: 'rendez_vous',
        operation: 'DELETE',
        recordId: rdv.id,
        metadata: { summary: `Suppression RDV — ${rdv.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          const { error } = await supabase.from('rendez_vous').delete().eq('id', rdv.id);
          if (error) throw error;
          return { id: rdv.id };
        },
      });
      try {
        await loadRendezVous();
        clearOptimisticCache();
      } catch (_) { /* rafraîchissement différé si offline */ }
    } catch (e: any) {
      alert(e.message || 'Erreur lors de la suppression');
    }
  };

  const handleModifierRdv = (rdv: RendezVous) => {
    setSelectedRdvToEdit(rdv);
    setShowModifierModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatutBadge = (rdv: RendezVous) => {
    switch (rdv.statut) {
      case 'en_attente': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 whitespace-nowrap">En attente</span>;
      case 'confirme': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">Confirmé</span>;
      case 'en_cours': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap">En cours</span>;
      case 'termine': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">Terminé</span>;
      case 'annule': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 whitespace-nowrap">Annulé</span>;
      case 'manque': return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 whitespace-nowrap">Manqué</span>;
      default: return null;
    }
  };

  const filteredRdv = rendezVous.filter(rdv => {
    const search = searchTerm.toLowerCase();
    const patientMatch = rdv.patients ? `${rdv.patients.nom} ${rdv.patients.prenom}`.toLowerCase().includes(search) : false;
    const medecinMatch = rdv.medecins ? `dr ${rdv.medecins.nom} ${rdv.medecins.prenom}`.toLowerCase().includes(search) : false;
    const motifMatch = rdv.motif ? rdv.motif.toLowerCase().includes(search) : false;
    const dateMatch = rdv.date_rdv ? formatDate(rdv.date_rdv).toLowerCase().includes(search) : false;

    if (search && !patientMatch && !medecinMatch && !motifMatch && !dateMatch) return false;
    if (filterMedecin && rdv.medecin_id !== filterMedecin) return false;
    if (filterStatut && rdv.statut !== filterStatut) return false;
    return true;
  });

  const stats = {
    total: rendezVous.length,
    enAttente: rendezVous.filter(r => r.statut === 'en_attente').length,
    confirmes: rendezVous.filter(r => r.statut === 'confirme').length,
    enCours: rendezVous.filter(r => r.statut === 'en_cours').length,
    aujourdhui: rendezVous.filter(r => r.date_rdv === new Date().toISOString().split('T')[0]).length,
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all cursor-pointer">
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestion des Rendez-vous</h1>
                <p className="mt-1 text-gray-600">Ajoutez, modifiez ou supprimez les rendez-vous de tous les médecins</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/secretaire/nouveau-rendez-vous')}
              className="flex items-center gap-2 px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line text-lg"></i>
              Nouveau rendez-vous
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-gray-100">
              <p className="text-sm text-gray-600 mb-1">Total</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-yellow-100">
              <p className="text-sm text-gray-600 mb-1">En attente</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.enAttente}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-green-100">
              <p className="text-sm text-gray-600 mb-1">Confirmés</p>
              <p className="text-3xl font-bold text-green-600">{stats.confirmes}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-orange-100">
              <p className="text-sm text-gray-600 mb-1">En cours</p>
              <p className="text-3xl font-bold text-orange-600">{stats.enCours}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-blue-100">
              <p className="text-sm text-gray-600 mb-1">Aujourd'hui</p>
              <p className="text-3xl font-bold text-blue-600">{stats.aujourdhui}</p>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par patient, médecin, date ou motif..."
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>
              <select
                value={filterMedecin}
                onChange={(e) => setFilterMedecin(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm cursor-pointer min-w-[200px]"
              >
                <option value="">Tous les médecins</option>
                {medecins.map(m => (
                  <option key={m.id} value={m.id}>Dr. {m.nom} {m.prenom} - {m.specialite}</option>
                ))}
              </select>
              <select
                value={filterStatut}
                onChange={(e) => setFilterStatut(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm cursor-pointer"
              >
                <option value="">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="confirme">Confirmé</option>
                <option value="en_cours">En cours</option>
                <option value="termine">Terminé</option>
                <option value="annule">Annulé</option>
                <option value="manque">Manqué</option>
              </select>
            </div>
          </div>

          {/* Liste des rendez-vous */}
          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-gray-100">
            {filteredRdv.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-calendar-line text-4xl text-clinic-blue"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun rendez-vous trouvé</h3>
                <p className="text-gray-600">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRdv.map((rdv) => (
                  <div
                    key={rdv.id}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      rdv.statut === 'en_cours' ? 'border-orange-300 bg-orange-50/30' :
                      rdv.statut === 'annule' ? 'border-red-200 bg-red-50/20' :
                      rdv.statut === 'termine' ? 'border-blue-200 bg-blue-50/20' :
                      'border-gray-200 hover:border-clinic-blue/30'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            rdv.statut === 'en_cours' ? 'bg-orange-500' :
                            rdv.statut === 'annule' ? 'bg-red-400' :
                            rdv.statut === 'termine' ? 'bg-blue-400' :
                            'bg-clinic-blue'
                          }`}>
                            <i className="ri-calendar-check-line text-xl text-white"></i>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-gray-900">
                              {rdv.date_rdv ? formatDate(rdv.date_rdv) : 'Date non définie'}
                            </p>
                            <p className="text-clinic-blue font-semibold">
                              {rdv.heure_debut?.substring(0, 5) || '??:??'} - {rdv.heure_fin?.substring(0, 5) || '??:??'}
                            </p>
                          </div>
                        </div>

                        <div className="ml-16 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <i className="ri-user-line text-clinic-blue"></i>
                            <p className="text-gray-700">
                              <strong>Patient :</strong> {rdv.patients ? `${rdv.patients.nom} ${rdv.patients.prenom}` : 'Inconnu'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-stethoscope-line text-clinic-blue"></i>
                            <p className="text-gray-700">
                              <strong>Médecin :</strong> {rdv.medecins ? `Dr. ${rdv.medecins.nom} ${rdv.medecins.prenom}` : 'Inconnu'}
                            </p>
                          </div>
                          {rdv.motif && (
                            <div className="flex items-start gap-2 md:col-span-2">
                              <i className="ri-file-text-line text-clinic-blue mt-0.5"></i>
                              <p className="text-gray-600 text-sm">{rdv.motif}</p>
                            </div>
                          )}
                          {rdv.patients?.telephone && (
                            <div className="flex items-center gap-2">
                              <i className="ri-phone-line text-clinic-blue"></i>
                              <a href={`tel:${rdv.patients.telephone}`} className="text-gray-700 hover:text-clinic-blue">{rdv.patients.telephone}</a>
                            </div>
                          )}
                          {rdv.patients?.email && (
                            <div className="flex items-center gap-2">
                              <i className="ri-mail-line text-clinic-blue"></i>
                              <a href={`mailto:${rdv.patients.email}`} className="text-gray-700 hover:text-clinic-blue">{rdv.patients.email}</a>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        {getStatutBadge(rdv)}

                        <div className="flex flex-wrap gap-2 justify-end">
                          {(rdv.statut === 'en_attente' || rdv.statut === 'confirme') && (
                            <>
                              <button
                                onClick={() => handleModifierRdv(rdv)}
                                className="flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-100 transition-all whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-edit-line"></i>
                                Modifier
                              </button>
                              <button
                                onClick={() => handleAnnulerRdv(rdv)}
                                className="flex items-center gap-1 px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-medium hover:bg-red-100 transition-all whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-close-circle-line"></i>
                                Annuler
                              </button>
                            </>
                          )}
                          {rdv.statut === 'en_cours' && (
                            <button
                              onClick={() => handleAnnulerRdv(rdv)}
                              className="flex items-center gap-1 px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-medium hover:bg-red-100 transition-all whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-close-circle-line"></i>
                              Annuler
                            </button>
                          )}
                          {isTrueSuperAdmin && (
                            <button
                              onClick={() => handleSupprimerRdv(rdv)}
                              className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ModifierRendezVousModal
        isOpen={showModifierModal}
        rendezVous={selectedRdvToEdit}
        onClose={() => { setShowModifierModal(false); setSelectedRdvToEdit(null); }}
        onSuccess={loadRendezVous}
      />

      <Footer />
    </div>
  );
}